/**
 * Tiskárny (modul Print Server, záložka Statistiky): cena černobílé a barevné
 * stránky, násobek A3, „jen černobíle" a popisek per tiskárna zákazníka a
 * „Ceny ve výchozím stavu zobrazovat" (počáteční stav přepínače Zobrazit ceny
 * pro toho, kdo si ho sám nenastavil). Čtení printserver.printer_settings
 * (správce), zápis printer_settings_set a pricing_set (printserver:admin).
 * Aplikace Tisky tuto část nemá.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  Input,
  Label,
  Modal,
  Toggle,
} from '../../components/ui';
import { api } from '../../lib/api';
import { explainPrintError, PrintCallError, unwrap } from './print-stats-call';
import { formatMoney, parsePriceInput } from './print-stats-logic';
import type { PrinterSettingsItem, PrinterSettingsResult, ToolFail } from './print-stats-types';

interface Draft {
  printer: string;
  price_mono: string;
  price_color: string;
  a3_multiplier: string;
  mono_only: boolean;
  label: string;
}

function draftOf(item: PrinterSettingsItem, language: string): Draft {
  const num = (n: number) => language.startsWith('en') ? String(n) : String(n).replace('.', ',');
  return {
    printer: item.printer,
    price_mono: num(item.price_mono),
    price_color: num(item.price_color),
    a3_multiplier: num(item.a3_multiplier),
    mono_only: item.mono_only,
    label: item.label ?? '',
  };
}

/** Kotva části Tiskárny (nápověda „Ceny tiskáren nejsou nastavené" na ni posune stránku). */
export const PRINTERS_SECTION_ID = 'printserver-printers';

/** printserver.printer_settings — sdílí ho část Tiskárny i přehled (smí správce ceny měnit). */
export function usePrinterSettings(
  scope: { customer?: string },
  scopeKey: string,
  enabled = true,
) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ['printserver', 'pricing', scopeKey],
    enabled,
    retry: false,
    queryFn: async () =>
      unwrap(
        await api.mcp<PrinterSettingsResult | ToolFail>('printserver.printer_settings', scope, {
          silent: true,
        }),
        t('printserver.pricing.loadFailed'),
      ),
  });
}

export function PrinterPricingCard(
  { scope, scopeKey }: { scope: { customer?: string }; scopeKey: string },
) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const listQ = usePrinterSettings(scope, scopeKey);

  const pricingEnabled = !!listQ.data?.pricing_enabled;

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['printserver', 'pricing'] });
    void qc.invalidateQueries({ queryKey: ['printserver', 'stats'] });
    void qc.invalidateQueries({ queryKey: ['printserver', 'jobs'] });
  };

  const togglePricing = useMutation({
    mutationFn: async (enabled: boolean) =>
      unwrap(
        await api.mcp<{ ok: true } | ToolFail>('printserver.pricing_set', { ...scope, enabled }, {
          silent: true,
        }),
        t('printserver.pricing.saveFailed'),
      ),
    onSuccess: () => {
      setFormError(null);
      refreshAll();
    },
    onError: (error: Error) =>
      setFormError(explainPrintError(t, error, t('printserver.pricing.saveFailed'))),
  });

  const save = useMutation({
    mutationFn: async (value: Draft) => {
      const prices = {
        price_mono: parsePriceInput(value.price_mono),
        price_color: parsePriceInput(value.price_color),
        a3_multiplier: parsePriceInput(value.a3_multiplier, 10),
      };
      if (Object.values(prices).some((p) => p === 'invalid')) {
        throw new PrintCallError(
          prices.a3_multiplier === 'invalid' ? 'invalid_a3_multiplier' : 'invalid_price',
          [],
        );
      }
      return unwrap(
        await api.mcp<{ ok: true } | ToolFail>('printserver.printer_settings_set', {
          ...scope,
          printer: value.printer,
          ...(prices.price_mono !== null ? { price_mono: prices.price_mono } : {}),
          ...(prices.price_color !== null ? { price_color: prices.price_color } : {}),
          a3_multiplier: prices.a3_multiplier,
          mono_only: value.mono_only,
          label: value.label.trim() || null,
        }, { silent: true }),
        t('printserver.pricing.saveFailed'),
      );
    },
    onSuccess: () => {
      setDraft(null);
      setFormError(null);
      refreshAll();
    },
    onError: (error: Error) =>
      setFormError(explainPrintError(t, error, t('printserver.pricing.saveFailed'))),
  });

  // Bez práva číst ceny (aplikace, čtenář bez správy) se část vůbec neukáže.
  if (listQ.error instanceof PrintCallError && listQ.error.code === 'forbidden') return null;

  const data = listQ.data;
  const currency = data?.currency ?? 'CZK';
  const money = (n: number) => formatMoney(n, i18n.language, currency);
  const canEdit = !!data?.can_edit;

  return (
    <div id={PRINTERS_SECTION_ID}>
      <Card>
        <CardContent>
          <h3 className='mb-1 text-sm font-medium'>{t('printserver.pricing.title')}</h3>
          <p className='mb-3 text-xs text-gray-400'>{t('printserver.pricing.intro')}</p>
          {data && (
            <div className='mb-3 flex flex-wrap items-center gap-3'>
              {canEdit
                ? (
                  <Toggle
                    label={t('printserver.pricing.enable')}
                    value={pricingEnabled}
                    saving={togglePricing.isPending}
                    onSave={(value) => togglePricing.mutateAsync(value).then(() => undefined)}
                  />
                )
                : (
                  <Badge variant={pricingEnabled ? 'success' : 'default'}>
                    {pricingEnabled
                      ? t('printserver.pricing.enabledOn')
                      : t('printserver.pricing.enabledOff')}
                  </Badge>
                )}
              <span className='text-xs text-gray-400'>{t('printserver.pricing.defaultHint')}</span>
            </div>
          )}
          {formError && !draft && <Alert variant='destructive'>{formError}</Alert>}
          {listQ.isError && (
            <Alert variant='destructive'>
              {explainPrintError(t, listQ.error, t('printserver.pricing.loadFailed'))}
            </Alert>
          )}
          {listQ.isLoading && (
            <p className='text-sm text-gray-400'>{t('printserver.stats.loading')}</p>
          )}
          {data && data.printers.length === 0 && (
            <EmptyState
              title={t('printserver.pricing.empty')}
            />
          )}
          {data && data.printers.length > 0 && (
            <DataTable
              data={data.printers as Array<PrinterSettingsItem & Record<string, unknown>>}
              rowKey={(row) => row.printer}
              searchable
              searchPlaceholder={t('printserver.pricing.search')}
              searchText={(row) => `${row.printer} ${row.label ?? ''}`}
              columns={[
                {
                  key: 'printer',
                  label: t('printserver.stats.colPrinter'),
                  sortable: true,
                  render: (row) => (
                    <span>
                      {row.label ? `${row.label} (${row.printer})` : row.printer}
                      {!row.configured && (
                        <span className='ml-2'>
                          <Badge variant='warning'>{t('printserver.pricing.noPrice')}</Badge>
                        </span>
                      )}
                    </span>
                  ),
                },
                ...[
                  {
                    key: 'price_mono',
                    label: t('printserver.pricing.priceMono'),
                    sortable: true,
                    render: (row: PrinterSettingsItem) => money(row.price_mono),
                  },
                  {
                    key: 'price_color',
                    label: t('printserver.pricing.priceColor'),
                    sortable: true,
                    render: (row: PrinterSettingsItem) =>
                      row.mono_only ? '—' : money(row.price_color),
                  },
                  {
                    key: 'a3_multiplier',
                    label: t('printserver.pricing.a3'),
                    sortable: true,
                    render: (row: PrinterSettingsItem) => `× ${row.a3_multiplier}`,
                  },
                ],
                {
                  key: 'mono_only',
                  label: t('printserver.pricing.monoOnly'),
                  sortable: true,
                  sortValue: (row) => (row.mono_only ? 1 : 0),
                  render: (row) =>
                    row.mono_only
                      ? <Badge variant='info'>{t('printserver.stats.yes')}</Badge>
                      : t('printserver.stats.no'),
                },
                { key: 'jobs', label: t('printserver.stats.colJobs'), sortable: true },
                ...(canEdit
                  ? [{
                    key: 'action',
                    label: t('printserver.stats.colAction'),
                    render: (row: PrinterSettingsItem) => (
                      <Button
                        size='sm'
                        variant='secondary'
                        icon={Pencil}
                        onClick={(event) => {
                          event.stopPropagation();
                          setFormError(null);
                          setDraft(draftOf(row, i18n.language));
                        }}
                      >
                        {t('printserver.pricing.edit')}
                      </Button>
                    ),
                  }]
                  : []),
              ]}
            />
          )}
        </CardContent>

        <Modal
          open={!!draft}
          onClose={() => setDraft(null)}
          title={t('printserver.pricing.editTitle', { printer: draft?.printer ?? '' })}
          size='md'
          footer={
            <div className='flex justify-end gap-2'>
              <Button variant='secondary' size='md' onClick={() => setDraft(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant='primary'
                size='md'
                loading={save.isPending}
                onClick={() => draft && save.mutate(draft)}
              >
                {t('common.save')}
              </Button>
            </div>
          }
        >
          {draft && (
            <div className='space-y-4'>
              <div className='grid gap-3 sm:grid-cols-2'>
                <div>
                  <Label>{t('printserver.pricing.priceMono')}</Label>
                  <Input
                    size='md'
                    inputMode='decimal'
                    value={draft.price_mono}
                    onChange={(event) => setDraft({ ...draft, price_mono: event.target.value })}
                  />
                </div>
                <div>
                  <Label>{t('printserver.pricing.priceColor')}</Label>
                  <Input
                    size='md'
                    inputMode='decimal'
                    disabled={draft.mono_only}
                    value={draft.price_color}
                    onChange={(event) => setDraft({ ...draft, price_color: event.target.value })}
                  />
                </div>
                <div>
                  <Label>{t('printserver.pricing.a3')}</Label>
                  <Input
                    size='md'
                    inputMode='decimal'
                    value={draft.a3_multiplier}
                    onChange={(event) => setDraft({ ...draft, a3_multiplier: event.target.value })}
                  />
                </div>
                <div>
                  <Label>{t('printserver.pricing.label')}</Label>
                  <Input
                    size='md'
                    maxLength={100}
                    value={draft.label}
                    onChange={(event) => setDraft({ ...draft, label: event.target.value })}
                  />
                </div>
              </div>
              <Toggle
                label={t('printserver.pricing.monoOnlyLong')}
                value={draft.mono_only}
                onSave={(value) => setDraft({ ...draft, mono_only: value })}
              />
              <p className='text-xs text-gray-400'>
                {t('printserver.pricing.hint')}
              </p>
              {formError && <Alert variant='destructive'>{formError}</Alert>}
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
}
